import { TextInput, Textarea } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { updateProfile } from '../../fetchHooks/updateProfile';

interface UserInputDebouncedProps {
  label: string;
  propertyName: string;
  userId?: number;
  value?: string | number;
  style?: React.CSSProperties;
  textField?: boolean;
  type?: 'text' | 'date' | 'number';
  disabled?: boolean;
  description?: string;
}

export const UserInputDebounced = ({
  label,
  propertyName,
  userId,
  value,
  style,
  textField = false,
  type = 'text',
  disabled = false,
  description,
}: UserInputDebouncedProps) => {
  const [inputValue, setInputValue] = useState(value);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedValue, setLastSavedValue] = useState(value);
  const queryClient = useQueryClient();

  // Check if the current input value matches the last saved value
  const isValueUpdated = inputValue === lastSavedValue && !isSaving;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = event?.target?.value;
    setInputValue(newValue as string);

    // Clear previous timer if any
    if (debounceTimer) clearTimeout(debounceTimer);

    // Set a new timer to send the PUT request after 2 seconds
    const newDebounceTimer = setTimeout(() => {
      setIsSaving(true);
      updateProfile({ [propertyName]: newValue, id: userId }, () => {
        setLastSavedValue(newValue as string);
        setIsSaving(false);
        void queryClient.invalidateQueries({ queryKey: ['userInfo'] });
      });
    }, 2000);

    setDebounceTimer(newDebounceTimer as NodeJS.Timeout);
  };

  const handleDateChange = (value: string | null) => {
    const newValue = value || '';
    setInputValue(newValue);

    // Clear previous timer if any
    if (debounceTimer) clearTimeout(debounceTimer);

    // Set a new timer to send the PUT request after 2 seconds
    const newDebounceTimer = setTimeout(() => {
      setIsSaving(true);
      updateProfile({ [propertyName]: newValue, id: userId }, () => {
        setLastSavedValue(newValue);
        setIsSaving(false);
        void queryClient.invalidateQueries({ queryKey: ['userInfo'] });
      });
    }, 2000);

    setDebounceTimer(newDebounceTimer as NodeJS.Timeout);
  };

  useEffect(() => {
    // Clear the timer when the component unmounts or when inputValue changes
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [debounceTimer]);

  // Update lastSavedValue when the value prop changes (after successful save)
  useEffect(() => {
    if (value !== undefined && value !== null) {
      setLastSavedValue(value);
      setInputValue(value);
    }
  }, [value]);
  if (!userId) return null;

  const getErrorMessage = () => {
    if (isSaving) return 'Saving...';
    if (!isValueUpdated) return 'Unsaved changes';
    return undefined;
  };

  const renderInput = () => {
    if (textField)
      return (
        <Textarea
          placeholder={label}
          value={inputValue || ''}
          error={getErrorMessage()}
          onChange={(value) => handleInputChange(value as React.ChangeEvent<HTMLTextAreaElement>)}
          style={style}
          disabled={disabled}
        />
      );
    if (type === 'date')
      return <DatePicker onChange={handleDateChange} style={style} value={inputValue ? new Date(inputValue) : null} />;

    return (
      <TextInput
        label={label}
        placeholder={label}
        value={inputValue || ''}
        error={getErrorMessage()}
        onChange={handleInputChange}
        style={style}
        type={type}
        disabled={disabled}
        description={description}
      />
    );
  };

  return <div style={{ marginBottom: '1rem' }}>{renderInput()}</div>;
};
export default UserInputDebounced;
